/**
 * Incremental mermaid parser for streaming LLM responses.
 * Parses %% groups declaration first, then colors nodes by their
 * inline group as edge lines stream in.
 */

interface GraphNode {
  id: number;
  label: string;
  uuid: string;
  color?: { border: string };
  orderIndex?: number;
}

interface GraphEdge {
  from: number;
  to: number;
  label?: string;
}

const GROUP_COLORS = [
  "#E07B7B", // soft red
  "#7BC8E0", // soft blue
  "#7BE08B", // soft green
  "#E0C77B", // soft yellow
  "#B07BE0", // soft purple
];

const EDGE_REGEX = /^(\w+)(?:\[([^\]]*)\])?\s*--+>(?:\|([^|]*)\|)?\s*(\w+)(?:\[([^\]]*)\])?$/;

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

class IncrementalMermaidParser {
  private buffer = '';
  private nodesMap = new Map<string, GraphNode>();
  private edges: GraphEdge[] = [];
  private idCounter = 1;
  private parsedLineCount = 0;
  private groupOrder: string[] = [];
  private nodeOrderCounter = 1;

  /**
   * Append a new chunk of streamed text and parse any new complete lines.
   * Returns the current graph if new nodes/edges were found, or null if nothing new.
   */
  append(chunk: string): { nodes: GraphNode[]; edges: GraphEdge[] } | null {
    this.buffer += chunk;

    const lines = this.buffer.split('\n');
    const completeLines = lines.slice(0, -1);

    if (completeLines.length <= this.parsedLineCount) return null;

    const prevNodeCount = this.nodesMap.size;
    const prevEdgeCount = this.edges.length;

    for (let i = this.parsedLineCount; i < completeLines.length; i++) {
      const line = completeLines[i].trim();
      if (!line || /^graph\s/i.test(line) || line.startsWith('```') || line.startsWith('%% title:')) continue;

      // Parse %% groups declaration for color ordering
      if (line.startsWith('%% groups:')) {
        this.groupOrder = line.slice(10).trim().split(',').map(s => s.trim());
        continue;
      }

      // Handle %% undo nX --> nY (with or without |label|)
      if (line.startsWith('%% undo')) {
        const undoMatch = line.match(/%% undo\s+(\w+)\s*--+>(?:\|[^|]*\|)?\s*(\w+)/);
        if (undoMatch) {
          const fromNode = this.nodesMap.get(undoMatch[1]);
          const toNode = this.nodesMap.get(undoMatch[2]);
          if (fromNode && toNode) {
            this.edges = this.edges.filter(e => !(e.from === fromNode.id && e.to === toNode.id));
          }
        }
        continue;
      }

      // Handle %% rename nX New Label
      if (line.startsWith('%% rename')) {
        const renameMatch = line.match(/%% rename\s+(\w+)\s+(.+)/);
        if (renameMatch) {
          const node = this.nodesMap.get(renameMatch[1]);
          if (node) {
            node.label = renameMatch[2].trim();
          }
        }
        continue;
      }

      // Handle %% remove nX (remove node and all its edges)
      if (line.startsWith('%% remove')) {
        const removeMatch = line.match(/%% remove\s+(\w+)/);
        if (removeMatch) {
          const node = this.nodesMap.get(removeMatch[1]);
          if (node) {
            this.edges = this.edges.filter(e => e.from !== node.id && e.to !== node.id);
            this.nodesMap.delete(removeMatch[1]);
          }
        }
        continue;
      }

      if (line.startsWith('%%')) continue;

      // Handle standalone node declarations: nX[Label | Group | pages]
      const nodeDecl = line.match(/^(\w+)\[([^\]]*)\]$/);
      if (nodeDecl) {
        this.getOrCreate(nodeDecl[1], nodeDecl[2]);
        continue;
      }

      const m = line.match(EDGE_REGEX);
      if (!m) continue;

      const [, fromId, fromRawLabel, edgeLabel, toId, toRawLabel] = m;
      const fromNode = this.getOrCreate(fromId, fromRawLabel);
      const toNode = this.getOrCreate(toId, toRawLabel);
      // Avoid duplicate edges
      const alreadyExists = this.edges.some(e => e.from === fromNode.id && e.to === toNode.id);
      if (!alreadyExists) {
        const edge: GraphEdge = { from: fromNode.id, to: toNode.id };
        if (edgeLabel) edge.label = edgeLabel.trim();
        this.edges.push(edge);
      }
    }

    this.parsedLineCount = completeLines.length;

    if (this.nodesMap.size === prevNodeCount && this.edges.length === prevEdgeCount) {
      return null;
    }

    return this.getGraph();
  }

  getGraph(): { nodes: GraphNode[]; edges: GraphEdge[] } {
    return {
      nodes: Array.from(this.nodesMap.values()),
      edges: [...this.edges],
    };
  }

  private getGroupColor(groupName: string): string | undefined {
    let idx = this.groupOrder.indexOf(groupName);
    if (idx === -1) {
      // Group not in declaration — append dynamically
      this.groupOrder.push(groupName);
      idx = this.groupOrder.length - 1;
    }
    return GROUP_COLORS[idx % GROUP_COLORS.length];
  }

  private getOrCreate(mermaidId: string, rawLabel: string | undefined): GraphNode {
    // Parse [Label | Group | pages] → extract label and group
    const parts = rawLabel ? rawLabel.split('|').map(s => s.trim()) : [];
    const label = parts[0] || undefined;
    const groupName = parts.length >= 2 ? parts[1] : undefined;

    if (this.nodesMap.has(mermaidId)) {
      const node = this.nodesMap.get(mermaidId)!;
      if (label && node.label === mermaidId) {
        node.label = label;
      }
      return node;
    }
    const node: GraphNode = {
      id: this.idCounter++,
      label: label || mermaidId,
      uuid: generateUUID(),
      orderIndex: this.nodeOrderCounter++,
    };
    if (groupName) {
      node.color = { border: this.getGroupColor(groupName)! };
    }
    this.nodesMap.set(mermaidId, node);
    return node;
  }
}

export default IncrementalMermaidParser;
